/**
 * Newcomers (v1 ticket 42), the DOM-free half of the web side: the capture panel's URL, which may carry the newcomer
 * moment a typed note answers (`/#type?answer=<id>`, so a reload keeps it), and the question Answer them shows.
 */
import type { DistrictPerson, NewcomersOffer } from './town/own-town';
import type { Moment } from './types';

/** The capture panel's tabs (capture-form.ts `CaptureTab`), and the lantern's (ticket 44: `/#listen`), and a shared voice message's (`/#shared`). */
export type CaptureTabName = 'record' | 'wanted' | 'listen' | 'shared';

/** Home's capture tab, the newcomer answered on Type, or the saved recording selected on Listen. */
export interface CaptureRoute { tab: CaptureTabName; answer?: string; draft?: string }

/** A moment id as the server makes them (a UUID or a draft's client id): letters, digits, `-` and `_`. */
const MOMENT_ID = /^[A-Za-z0-9_-]{1,100}$/;

/** The capture panel's route from home's hash, or null when the hash does not name it. */
export function captureRouteOf(hash: string): CaptureRoute | null {
  if (hash === '#record') return { tab: 'record' };
  if (hash === '#listen') return { tab: 'listen' };
  const draft = /^#listen\?draft=([^&#]*)$/.exec(hash);
  if (draft) {
    let id = '';
    try { id = decodeURIComponent(draft[1]); } catch { /* Keep the explicit selection unavailable. */ }
    return { tab: 'listen', draft: MOMENT_ID.test(id) ? id : '' };
  }
  if (hash === '#shared') return { tab: 'shared' };
  if (hash === '#type') return { tab: 'wanted' };
  const m = /^#type\?answer=([^&#]*)$/.exec(hash);
  if (!m) return null;
  if (!m[1]) return { tab: 'wanted' };
  let id: string;
  try { id = decodeURIComponent(m[1]); } catch { return { tab: 'wanted' }; }
  return MOMENT_ID.test(id) ? { tab: 'wanted', answer: id } : { tab: 'wanted' };
}

/** The hash for a route (`#record`, `#listen`, `#listen?draft=<id>`, `#shared`, `#type`, `#type?answer=<id>`). */
export function captureHashOf(r: CaptureRoute): string {
  if (r.tab === 'record') return '#record';
  if (r.tab === 'listen') return r.draft !== undefined ? `#listen?draft=${encodeURIComponent(r.draft)}` : '#listen';
  if (r.tab === 'shared') return '#shared';
  return r.answer ? `#type?answer=${encodeURIComponent(r.answer)}` : '#type';
}

/** A newcomer's question: who asks, the line in the target language (and its Finglish), its translation. */
export interface NewcomerQuestion { name: string; ca: string; latin?: string; en: string }

/**
 * The question a newcomer moment asks the learner (`analysis.scene.ask`), or null: not a newcomer moment, no kept
 * scene, or no such line. The server's `askOf` reads it the same way.
 */
export function questionOf(m: Pick<Moment, 'newcomers' | 'analysis'> | null | undefined): NewcomerQuestion | null {
  const sc = m?.analysis?.scene;
  const at = sc?.ask;
  const line = typeof at === 'number' ? m?.analysis?.transcript?.[at] : undefined;
  if (!m?.newcomers || !sc?.other || sc.dropped || !line?.ca) return null;
  return { name: sc.other.name || line.speaker, ca: line.ca, ...(line.latin ? { latin: line.latin } : {}), en: line.en };
}

/** Who a newcomer is (the revamp): their name (Finglish for a Persian name), its script, and the scene's role for them. */
export interface NewcomerPerson { name: string; script?: string; role?: string }

/**
 * The person a newcomer moment brought, or null: not a newcomer moment, or nobody named. The mark's name comes first
 * (a Persian name in Finglish, with its script); an older moment falls back to the scene's other person.
 */
export function personOf(m: Pick<Moment, 'newcomers' | 'analysis'> | null | undefined): NewcomerPerson | null {
  const mark = m?.newcomers;
  if (!mark) return null;
  const other = m?.analysis?.scene?.other;
  const name = mark.name || other?.name;
  if (!name) return null;
  const role = mark.role || other?.role;
  return { name, ...(mark.name && mark.script ? { script: mark.script } : {}), ...(role ? { role } : {}) };
}

/** The offer's kind: an older server's offer has none and is someone arriving. */
export function offerKind(o: NewcomersOffer): 'arrive' | 'waiting' {
  return o.kind === 'waiting' ? 'waiting' : 'arrive';
}

/** A district's newcomer: the revamp names them; an older server's `true` names nobody. */
export function districtPerson(n: true | DistrictPerson | undefined | null): DistrictPerson | null {
  return n && typeof n === 'object' && typeof n.name === 'string' && n.name ? n : null;
}
